import { useState, useEffect, useRef, useCallback } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Search, X } from "lucide-react";

/**
 * Debounced medication autocomplete with badge-style selection list.
 * Used inside the Medical Questionnaire "Medications & Allergies" step
 * (separate from the profile's DrugSearch — different styling + testids).
 */
export default function DrugSearchInline({ medications, setMedications }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setShowResults(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const search = useCallback(async (q) => {
    if (q.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const { data } = await API.get(`/drugs/search?q=${encodeURIComponent(q)}`);
      setResults(data.filter(d => !medications.includes(d)));
      setShowResults(true);
    } catch { setResults([]); } finally { setLoading(false); }
  }, [medications]);

  const handleInput = (val) => {
    setQuery(val);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => search(val), 400);
  };

  return (
    <div>
      <div ref={wrapperRef} className="relative">
        <div className="relative">
          <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <Input value={query} onChange={(e) => handleInput(e.target.value)}
            placeholder="Search medications..." className="rounded-xl pl-10" data-testid="q-drug-search" />
        </div>
        {loading && <div className="absolute right-3 top-3"><div className="w-4 h-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin" /></div>}
        {showResults && results.length > 0 && (
          <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-48 overflow-y-auto">
            {results.map((drug) => (
              <button key={`search-${drug}`} onClick={() => { if (!medications.includes(drug)) setMedications([...medications, drug]); setQuery(""); setShowResults(false); }}
                className="w-full text-left px-4 py-2.5 hover:bg-purple-50 border-b border-slate-100 last:border-0 text-sm text-slate-700">{drug}</button>
            ))}
          </div>
        )}
      </div>
      {medications.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="q-drug-list">
          {medications.map((drug) => (
            <div key={`med-${drug}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5">
              <div className="flex items-center gap-2"><div className="w-2 h-2 rounded-full bg-purple-400" /><span className="text-sm text-slate-700">{drug}</span></div>
              <button onClick={() => setMedications(medications.filter(m => m !== drug))} className="text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
